/**
 * Shared approval-detail helpers - turn a queued ApprovalItem into the
 * human-readable bits (tool label, recipient/subject fields, body preview,
 * risk label) that the inline approval card, the approvals queue dialog, and
 * the full preview dialog all render. One source so the three surfaces stay
 * consistent and the "never surface raw IDs" rule lives in exactly one place.
 */

export const APPROVAL_TOOL_LABEL: Record<string, string> = {
    send_email: 'Send email',
    email_reply_thread: 'Reply to email',
    teams_send_chat_to_person: 'Teams chat',
    teams_send_chat_message: 'Teams message',
    teams_send_channel_message: 'Teams channel post',
    teams_create_group_chat: 'Teams group chat',
    calendar_create_event: 'Create calendar event',
    calendar_update_event: 'Update calendar event',
    onedrive_share_link: 'Share OneDrive link',
    onedrive_put_file: 'Save OneDrive file',
    github_create_pr: 'Open GitHub PR',
    github_create_issue: 'Create GitHub issue',
    github_pr_comment: 'Comment on PR',
    jira_create_issue: 'Create Jira issue',
    jira_transition_issue: 'Transition Jira issue',
    jira_add_comment: 'Comment on Jira issue',
    confluence_create_page: 'Create Confluence page',
    confluence_update_page: 'Update Confluence page',
};

export function approvalToolLabel(toolId: string | null | undefined): string {
    const id = toolId ?? '';
    return APPROVAL_TOOL_LABEL[id] ?? id.replace(/_/g, ' ');
}

function titleWords(snake: string): string {
    return snake
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

/**
 * Verb+object phrase for the approval headline: "{DDR} wants approval to {phrase}".
 * Comms tools keep their send phrasing; MCP tool ids are parsed into
 * "use {Server}: {Tool}" instead of dumped raw, so the toast never reads
 * "wants approval to send mcp composio gmail gmail fetch emails".
 */
export function approvalActionPhrase(toolId: string | null | undefined): string {
    const id = toolId ?? '';
    switch (id) {
        case 'send_email':
            return 'send email';
        case 'email_reply_thread':
            return 'send an email reply';
        case 'teams_send_chat_to_person':
            return 'send a teams chat';
        case 'teams_send_chat_message':
            return 'send a teams message';
        case 'teams_send_channel_message':
            return 'post to a teams channel';
        case 'teams_create_group_chat':
            return 'create a teams group chat';
    }
    if (id.startsWith('mcp__')) {
        const parts = id.split('__');
        const server = titleWords(parts[1] ?? '');
        const tool = titleWords(parts.slice(2).join('_'));
        if (tool) {
            return server ? `use ${server}: ${tool}` : `use ${tool}`;
        }
        return server ? `use ${server}` : 'use a connected tool';
    }
    return approvalToolLabel(id).toLowerCase();
}

export type ApprovalDetailSource = {
    tool_id: string;
    payload?: Record<string, unknown> | null;
    edited_payload?: Record<string, unknown> | null;
};

export type ApprovalField = { label: string; value: string };

const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const _AAD_LIKE_RE = /^[0-9a-f-]{30,}$/i;

/** Hard rule: never surface raw IDs (UUIDs, AAD ids, opaque tokens) in a
 *  draft preview. Heuristic: an id-ish key with an opaque value, or any value
 *  that is itself a UUID. */
function looksLikeRawId(key: string, value: unknown): boolean {
    const k = key.toLowerCase();
    if (Array.isArray(value)) {
        return value.length > 0 && value.every(v => typeof v === 'string' && (_UUID_RE.test(v) || _AAD_LIKE_RE.test(v)));
    }
    const v = typeof value === 'string' ? value.trim() : '';
    if (_UUID_RE.test(v)) {
        return true;
    }
    if (k.endsWith('_id') || k.endsWith('_uuid') || k.endsWith('_user_id') || k.endsWith('_account_id') || k === 'id' || k === 'aad_id') {
        if (typeof value === 'string' && _AAD_LIKE_RE.test(v)) {
            return true;
        }
        if (Array.isArray(value)) {
            return true;
        }
    }
    return false;
}

/** The trust gate persists `{"args": {...real_args...}}` (gate.py), so always
 *  unwrap to the real tool arguments. Older approvals predate the wrapper and
 *  fall through to the raw payload. Prefers an edited payload when present. */
function extractArgs(item: ApprovalDetailSource): Record<string, unknown> {
    const payload = item.edited_payload ?? item.payload;
    if (!payload) {
        return {};
    }
    const inner = payload['args'];
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return inner as Record<string, unknown>;
    }
    return payload;
}

/** Recipient / subject / scheduling fields (everything that isn't the body),
 *  per tool type. Raw ids are dropped - the backend-resolved `summary` carries
 *  the human recipient name for id-only tools (Teams, Jira assignee, etc). */
export function approvalFields(item: ApprovalDetailSource): ApprovalField[] {
    const payload = extractArgs(item);
    const out: ApprovalField[] = [];
    const pick = (key: string, label: string): void => {
        const v = payload[key];
        if (v == null || v === '') {
            return;
        }
        const str = Array.isArray(v) ? v.map(x => String(x)).join(', ') : String(v);
        out.push({ label, value: str });
    };
    // `to` and `to_addresses` are interchangeable recipient keys; render only
    // the first present so we never emit two 'To' fields (same label → same
    // template track key → NG0955). Prefer `to`.
    const pickFirst = (keys: string[], label: string): void => {
        const key = keys.find(k => payload[k] != null && payload[k] !== '');
        if (key) {
            pick(key, label);
        }
    };

    switch (item.tool_id) {
        case 'send_email':
        case 'email_reply_thread':
            pickFirst(['to', 'to_addresses'], 'To');
            pick('cc', 'Cc');
            pick('bcc', 'Bcc');
            pick('subject', 'Subject');
            break;
        case 'teams_create_group_chat':
            pick('topic', 'Topic');
            break;
        case 'calendar_create_event':
        case 'calendar_update_event':
            pick('subject', 'Subject');
            pick('attendees', 'Attendees');
            pick('start_iso', 'Start');
            pick('end_iso', 'End');
            pick('location', 'Location');
            break;
        case 'jira_create_issue':
            pick('project_key', 'Project');
            pick('summary', 'Summary');
            pick('issue_type', 'Type');
            break;
        case 'github_create_pr':
            pick('owner', 'Owner');
            pick('repo', 'Repo');
            pick('title', 'Title');
            pick('head', 'From');
            pick('base', 'Into');
            break;
        case 'teams_send_chat_to_person':
        case 'teams_send_chat_message':
        case 'teams_send_channel_message':
            // Recipient ids are raw AAD/opaque tokens - the summary carries the
            // resolved name. No scalar field worth surfacing here.
            break;
        default:
            for (const [k, v] of Object.entries(payload)) {
                if (k === 'body' || k === 'description' || k === 'args') {
                    continue;
                }
                if (v == null || v === '') {
                    continue;
                }
                if (looksLikeRawId(k, v)) {
                    continue;
                }
                const str = Array.isArray(v) ? v.map(x => String(x)).join(', ') : String(v);
                out.push({ label: k.replace(/_/g, ' '), value: str });
            }
            break;
    }
    return out;
}

/** The outgoing message body / description / comment, whichever the tool
 *  carries. Returned in full - callers clamp the preview height in CSS. */
export function approvalBody(item: ApprovalDetailSource): string | null {
    const payload = extractArgs(item);
    for (const key of ['body', 'description', 'comment', 'note']) {
        const v = payload[key];
        if (typeof v === 'string' && v.trim()) {
            return v;
        }
    }
    return null;
}

export function approvalSubject(item: ApprovalDetailSource): string | null {
    const payload = extractArgs(item);
    const v = payload['subject'];
    if (typeof v === 'string' && v.trim()) {
        return v.trim();
    }
    // Replies carry no `subject` in args (just message_id + body) — the backend
    // stashes the real Re:-subject in payload.meta at queue time.
    const meta = item.payload?.['meta'] as { subject?: unknown } | undefined;
    return typeof meta?.subject === 'string' && meta.subject.trim() ? meta.subject.trim() : null;
}

/** The REAL recipient email address(es) for a send approval, snapshotted at
 *  queue time (payload.meta.recipient_emails). Shown in the To line so the user
 *  can security-check exactly who a send goes to — an email address is NOT a raw
 *  id to hide; for an outbound send it's the precise thing the user must verify.
 *  Empty for non-email tools / older rows lacking the snapshot. */
export function approvalRecipientEmails(item: ApprovalDetailSource): string[] {
    const meta = item.payload?.['meta'] as { recipient_emails?: unknown } | undefined;
    const list = meta?.recipient_emails;
    if (!Array.isArray(list)) {
        return [];
    }
    return list.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).map(x => x.trim());
}

/** The REAL cc address(es) for an email approval (payload.meta.cc_emails). Shown
 *  as a separate Cc line so the user sees everyone who gets the email, not just
 *  the To recipients. Empty when there's no cc / no snapshot. */
export function approvalCcEmails(item: ApprovalDetailSource): string[] {
    const meta = item.payload?.['meta'] as { cc_emails?: unknown } | undefined;
    const list = meta?.cc_emails;
    if (!Array.isArray(list)) {
        return [];
    }
    return list.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).map(x => x.trim());
}

/** Bound a joined recipient display for a compact card (#278): show the first
 *  `max` names and collapse the rest behind a "+N more" affordance, so a
 *  15-person send doesn't wall the approval card. Splits on commas, which is
 *  how every recipient display line in this file is joined. */
export function splitRecipientDisplay(display: string, max = 3): { shown: string; hidden: number } {
    const parts = display
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);
    if (parts.length <= max) {
        return { shown: display, hidden: 0 };
    }
    return { shown: parts.slice(0, max).join(', '), hidden: parts.length - max };
}

const SCOPE_TIER_LABEL: Record<string, string> = {
    command: 'You requested this',
    internal: 'Internal',
    'internal-only': 'Internal only',
    personal: 'Personal',
    'team-shared': 'Team-shared',
    flagged: 'Needs review',
    'external-vip': 'External VIP',
    'mixed-external': 'External recipients',
    'customer-repo': 'Customer repo',
    'org-public': 'Org-public',
    regulated: 'Regulated data',
    restricted: 'Restricted',
    'private-space': 'Private space',
    'sfdc-internal': 'Salesforce (internal)',
    'sfdc-customer-facing': 'Salesforce (customer-facing)',
};

/** Turn the internal scope_tier code into a short human risk/category label.
 *  Per-class (`hclass:{uuid}`) and per-entity (`entity:{id}`) cells carry raw
 *  ids - collapse them to a friendly label so no uuid ever hits the screen.
 *  Returns null for tiers not worth showing (unknown / empty). */
export function humanizeScopeTier(tier: string | null | undefined): string | null {
    if (!tier) {
        return null;
    }
    const t = tier.trim();
    if (!t || t.toLowerCase() === 'unknown') {
        return null;
    }
    if (t.startsWith('hclass:')) {
        return 'Learned rule';
    }
    if (t.startsWith('entity:')) {
        return 'Per-contact rule';
    }
    return SCOPE_TIER_LABEL[t.toLowerCase()] ?? t.replace(/[-_]/g, ' ');
}
